import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import {
  AppCard,
  AppNoInfo,
  AppNotificationAlert,
  AppSingleSelect,
} from "@/layout/AppComponents";
import styled from 'styled-components';
import { axiosPath } from '../../../../routing/helpers/router';
import Translator from '../../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import * as currentPageAction from '../../../../store/actions/currentPageAction';
import ModuleComsaEnvironmentalSideBarReports from '../ModuleComsaEnvironmentalSideBarReports';
import AppSubmitButton from '../../../../layout/components/form/AppSubmitButton';
import errorHandler from '@/layout/components/error/ErrorHandler';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';

export default function ModuleComsaEnvironmentalConstructionDesignScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [initialValues, setInitialValues] = useState();
  const [noYesOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="no" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="yes" />
      ),
      value: 1,
    },
  ]);

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          { moduleBridgeHex, subModuleBridgeHex, plantHex, sheet: "design" }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(
              r.data?.sheetData ?? {
                needVentilationSystem: null,
                needAgainstNoise: null,
                needAntivibratory: null,
                needContaminationRetention: null,
                needWaterPurifying: null,
                needCourseRestoration: null,
                needFloorRestoration: null,
                needVegetationRestoration: null,
                needHeritageProtection: null,
                needCorrectiveMeasures: null,
                environmentalAffection: null,
                needExpropriation: null,
                permeableInfrastructure: null,
              }
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  // > SAVE FORM //
  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "design",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < SAVE FORM //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="construction"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content pb-5">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_construction_design" />
        <Title className="mb-4">
          {" "}
          <Translator
            file="module-comsaEnvironmental"
            name="design.data.collection"
          />
        </Title>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <AppCard
                  header={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="general.design.parameters"
                    />
                  )}
                  className="mb-2"
                >
                  <AppSingleSelect
                    options={noYesOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="necessary.design.ventilation.air.treatment.system"
                      />
                    )}
                    value={values?.needVentilationSystem}
                    error={errors?.needVentilationSystem}
                    onChange={(opt) =>
                      setFieldValue("needVentilationSystem", opt)
                    }
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaEnvironmental"
                        name="necessary.design.permanent.noise.shielding"
                      />
                    )}
                    value={values?.needAgainstNoise}
                    error={errors?.needAgainstNoise}
                    onChange={(opt) => setFieldValue("needAgainstNoise", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="necessary.install.permanent.anti.vibration.protection.elements"
                      />
                    }
                    value={values?.needAntivibratory}
                    error={errors?.needAntivibratory}
                    onChange={(opt) => setFieldValue("needAntivibratory", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="necessary.install.contaminants.retention.elements.soil"
                      />
                    }
                    value={values?.needContaminationRetention}
                    error={errors?.needContaminationRetention}
                    onChange={(opt) =>
                      setFieldValue("needContaminationRetention", opt)
                    }
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="necessary.design.water.purification.systems"
                      />
                    }
                    value={values?.needWaterPurifying}
                    error={errors?.needWaterPurifying}
                    onChange={(opt) => setFieldValue("needWaterPurifying", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="necessary.design.stream.restoration"
                      />
                    }
                    value={values?.needCourseRestoration}
                    error={errors?.needCourseRestoration}
                    onChange={(opt) =>
                      setFieldValue("needCourseRestoration", opt)
                    }
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="necessary.design.soil.restoration"
                      />
                    }
                    value={values?.needFloorRestoration}
                    error={errors?.needFloorRestoration}
                    onChange={(opt) =>
                      setFieldValue("needFloorRestoration", opt)
                    }
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="necessary.design.vegetation.restoration"
                      />
                    }
                    value={values?.needVegetationRestoration}
                    error={errors?.needVegetationRestoration}
                    onChange={(opt) =>
                      setFieldValue("needVegetationRestoration", opt)
                    }
                    isClearable
                  />

                  <AppSingleSelect
                    options={noYesOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="measures.protection.heritage.necessary"
                      />
                    }
                    value={values?.needHeritageProtection}
                    error={errors?.needHeritageProtection}
                    onChange={(opt) =>
                      setFieldValue("needHeritageProtection", opt)
                    }
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="necessary.design.corrective.measures.reduce.visual.impact"
                      />
                    }
                    value={values?.needCorrectiveMeasures}
                    error={errors?.needCorrectiveMeasures}
                    onChange={(opt) =>
                      setFieldValue("needCorrectiveMeasures", opt)
                    }
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="environmental.impact.expected.due.planning.work.schedule.seasonal.periods"
                      />
                    }
                    value={values?.environmentalAffection}
                    error={errors?.environmentalAffection}
                    onChange={(opt) =>
                      setFieldValue("environmentalAffection", opt)
                    }
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="are.expropriations.necessary"
                      />
                    }
                    value={values?.needExpropriation}
                    error={errors?.needExpropriation}
                    onChange={(opt) => setFieldValue("needExpropriation", opt)}
                    isClearable
                  />
                  <AppSingleSelect
                    options={noYesOptions}
                    title={
                      <Translator
                        file="module-comsaEnvironmental"
                        name="infrastructure.permeable.existing.fauna.area"
                      />
                    }
                    value={values?.permeableInfrastructure}
                    error={errors?.permeableInfrastructure}
                    onChange={(opt) =>
                      setFieldValue("permeableInfrastructure", opt)
                    }
                    isClearable
                  />
                </AppCard>
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
              </form>
            )}
          </Formik>
        )}
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;
